<template>
  <v-container grid-list-md>
    <v-layout row wrap justify-center>
      <v-flex xs12 grow>
        <v-alert
          v-model="showMsg"
          dismissible
          :type="msgType"
          transition="fade-transition"
        >
          {{ msgBody }}
        </v-alert>
      </v-flex>

      <v-flex xs12 sm7>
        <v-btn
          block
          :loading="paypalUpdating"
          :disabled="paypalUpdating"
          @click="updatePaypalConfiguration"
        >
          Update PayPal Configuration
        </v-btn>
      </v-flex>

      <v-flex xs12 sm6>
        <v-card>
          <v-card-text>
            <v-layout row wrap>
              <v-flex xs12>
                <h1>Pricing Definitions</h1>
                <p>This will set the pricing scheme details used by new subscriptions, but existing subscriptions will not be affected.</p>
                <v-progress-linear :indeterminate="true" :active="pricingLoading" />
                <v-select
                  v-model="pricingDefinitionID"
                  :items="pricingDefinitions"
                  item-value="id"
                  label="Definition"
                />
                <v-textarea
                  disabled
                  v-model="pricing.description"
                  label="Description"
                />
                <v-text-field
                  v-model="pricing.price"
                  label="Price"
                  :error-messages="errors.pricing.price"
                  id="pricingPrice"
                />
                <v-select
                  v-model="pricing.billingTermsID"
                  :items="billingTerms"
                  item-value="id"
                  item-text="title"
                  label="Billing Terms"
                  :loading="billingTermsLoading"
                  :disabled="billingTermsLoading"
                  hint="Applies only to billing methods that use invoices"
                  :persistent-hint="true"
                />
                <v-text-field
                  v-model="pricing.billingCycles"
                  label="Billing Cycles"
                  :error-messages="errors.pricing.billingCycles"
                  id="pricingBillingCycles"
                  hint="Leave blank for an indefinite terminal date"
                  :persistent-hint="true"
                />
                <v-flex>
                  <v-btn
                    block
                    :loading="pricingUpdating"
                    :disabled="pricingUpdating"
                    @click="updatePricingDefinition"
                  >
                    Update
                  </v-btn>
                </v-flex>
                <v-btn
                  :disabled="pricingDefaultButtonIsDisabled"
                  block
                  :loading="pricingDefaultUpdating"
                  @click="updatePricingDefault"
                >
                  {{ pricing.isDefault ? 'Current Default' : 'Make Default' }}
                </v-btn>
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-card>
      </v-flex>

      <v-flex xs12 sm6>
        <v-card>
          <v-card-text>
            <v-layout row wrap>
              <v-flex xs12>
                <h1>Billing Methods</h1>
                <p>This will set the billing method used by new subscriptions, but existing subscriptions will not be affected.</p>
                <v-progress-linear :indeterminate="true" :active="billingMethodsLoading" />
                <v-select
                  v-model="billingMethodID"
                  :items="billingMethods"
                  item-text="title"
                  item-value="id"
                  label="Method"
                  hint="Some options are disabled if PayPal is not configured"
                  :persistent-hint="true"
                />
                <v-btn
                  :disabled="billingMethodDefaultButtonIsDisabled"
                  block
                  :loading="billingMethodDefaultUpdating"
                  @click="updateBillingMethodDefault"
                >
                  {{ billingMethod.isDefault ? 'Current Default' : 'Make Default' }}
                </v-btn>
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
export default {
  data: () => ({
    paypalUpdating: false,

    productLoading: false,
    product: {},

    billingTermsLoading: false,
    billingTerms: [],

    pricingLoading: false,
    pricingDefinitions: null,
    pricingDefinitionID: null,
    pricingUpdating: false,
    pricingDefaultUpdating: false,

    billingMethodsLoading: false,
    billingMethods: null,
    billingMethodID: null,
    billingMethodUpdating: false,
    billingMethodDefaultUpdating: false,

    showMsg: false,
    msgType: 'error',
    msgBody: null,
    errors: {
      pricing: {}
    }
  }),
  computed: {
    pricing: function () {
      // Make sure we have definitions loaded
      if (this.pricingDefinitions !== null && this.pricingDefinitionID !== null) {
        // Find the index of our current ID
        let i = this.pricingDefinitions.findIndex((def) => {
          return def.id === this.pricingDefinitionID
        })

        // Return the definition or an empty object if we couldn't find the ID
        return (i === -1) ? {} : this.pricingDefinitions[i]
      } else {
        return {}
      }
    },
    billingMethod: function () {
      // Make sure we have methods loaded
      if (this.billingMethods !== null && this.billingMethodID !== null) {
        // Find the index of our current ID
        let i = this.billingMethods.findIndex((method) => {
          return method.id === this.billingMethodID
        })

        // Return the method or an empty object if we couldn't find the ID
        return (i === -1) ? {} : this.billingMethods[i]
      } else {
        return {}
      }
    },
    pricingDefaultButtonIsDisabled: function () {
      // The button should always be disabled if the current pricing definition
      // is already the default, and also if it's not default but is currently
      // making the request to set it as default
      if (this.pricing.isDefault || this.pricingDefaultUpdating) {
        return true
      } else {
        return false
      }
    },
    billingMethodDefaultButtonIsDisabled: function () {
      // Same logic as pricing default
      if (this.billingMethod.isDefault || this.billingMethodDefaultUpdating) {
        return true
      } else {
        return false
      }
    }
  },
  mounted: function () {
    // Product Definition
    this.productLoading = true
    this.axios.get('/subscriptions/product/definition')
      .then((res) => {
        this.product = res.data
        this.productLoading = false

        // Billing Methods (dependent on loaded product information)
        this.billingMethodsLoading = true
        this.axios.get('/subscriptions/billing/methods')
          .then((res) => {
            // Mark any PayPal-dependent methods as disabled if PayPal isn't configured
            if (!this.product.ppID) {
              for (let i in res.data) {
                if (res.data[i].requiresPaypal) {
                  res.data[i].disabled = true
                }
              }
            }

            this.billingMethods = res.data
            this.billingMethodID = (res.data.find((method) => {
              return !!method.isDefault
            })).id

            this.billingMethodsLoading = false
          })
          .catch(this.$root.apiErrorHandler((msg) => {
            this.msgBody = msg
            this.msgType = 'error'
            this.showMsg = true
          }))
      })
      .catch(this.$root.apiErrorHandler((msg) => {
        this.msgBody = msg
        this.msgType = 'error'
        this.showMsg = true
      }))

    // Billing Terms
    this.billingTermsLoading = true
    this.axios.get('/subscriptions/billing/terms')
      .then((res) => {
        this.billingTerms = res.data
      })
      .catch(this.$root.apiErrorHandler((msg) => {
        this.msgBody = msg
        this.msgType = 'error'
        this.showMsg = true
      }))
      .finally(() => {
        this.billingTermsLoading = false
      })

    // Pricing Definitions
    this.pricingLoading = true
    this.axios.get('/subscriptions/pricing/definitions')
      .then((res) => {
        // Generate a display value for each pricing definition
        res.data.forEach((def) => {
          def.text = def.title
          if (def.supportsPaypal) {
            def.text += ' (supports PayPal)'
          }
        })

        this.pricingDefinitions = res.data
        this.pricingDefinitionID = (res.data.find((def) => {
          return !!def.isDefault
        })).id
        this.pricingLoading = false
      })
      .catch(this.$root.apiErrorHandler((msg) => {
        this.msgBody = msg
        this.msgType = 'error'
        this.showMsg = true
      }))
  },
  methods: {
    updatePaypalConfiguration: function () {
      this.paypalUpdating = true
      this.axios.post('/subscriptions/billing/paypal/setup')
        .then((res) => {
          this.msgType = 'success'

          // If we actually made a change, refresh the page
          if (res.status === 201) {
            setTimeout(window.location.reload.bind(window.location), 1500)
            this.msgBody = 'Changes made. Reloading page...'
          } else {
            this.msgBody = 'No changes required'
          }

          this.showMsg = true
        })
        .catch(this.$root.apiErrorHandler((msg) => {
          this.msgBody = msg
          this.msgType = 'error'
          this.showMsg = true
        }))
        .finally(() => {
          this.paypalUpdating = false
        })
    },
    updatePricingDefinition: function () {
      this.pricingUpdating = true

      // Build the data that we want to send
      let data = {
        price: this.pricing.price,
        billingCycles: this.pricing.billingCycles || null,
        billingTermsID: this.pricing.billingTermsID
      }

      this.axios.put('/subscriptions/pricing/definitions/' + this.pricing.id, data)
        .then(() => {
          this.msgBody = 'Updated successfully'
          this.msgType = 'success'
          this.showMsg = true
        })
        .catch(this.$root.apiErrorHandler((msg, errors) => {
          this.msgBody = msg
          this.errors.pricing = errors
          this.msgType = 'error'
          this.showMsg = true
        }))
        .finally(() => {
          this.pricingUpdating = false
        })
    },
    updatePricingDefault: function () {
      this.pricingDefaultUpdating = true
      this.axios.put('/subscriptions/pricing/definitions/default/' + this.pricing.id)
        .then(() => {
          this.msgBody = 'Updated successfully'
          this.msgType = 'success'
          this.showMsg = true

          // Update our pricing definitions so our current object is exclusively default
          this.pricingDefinitions.forEach((def) => {
            def.isDefault = false
          })
          this.pricing.isDefault = true
        })
        .catch(this.$root.apiErrorHandler((msg) => {
          this.msgBody = msg
          this.msgType = 'error'
          this.showMsg = true
        }))
        .finally(() => {
          this.pricingDefaultUpdating = false
        })
    },
    updateBillingMethodDefault: function () {
      this.billingMethodDefaultUpdating = true
      this.axios.put('/subscriptions/billing/methods/default/' + this.billingMethod.id)
        .then(() => {
          this.msgBody = 'Updated successfully'
          this.msgType = 'success'
          this.showMsg = true

          // Update our billing methods so our current object is exclusively default
          this.billingMethods.forEach((method) => {
            method.isDefault = false
          })
          this.billingMethod.isDefault = true
        })
        .catch(this.$root.apiErrorHandler((msg) => {
          this.msgBody = msg
          this.msgType = 'error'
          this.showMsg = true
        }))
        .finally(() => {
          this.billingMethodDefaultUpdating = false
        })
    }
  }
}
</script>
